import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, Redirect, Link } from "react-router-dom";

import "../../index.scss";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Select from "react-select";

import Sallie from "../Sallie";
import PillButton from "../StyledComponents/PillButton";
import ComponentWrapper from "./ComponentWrapper";
import SmLinkButton from "../StyledComponents/smLinkButton";
import GlobalSearchBar from "./GlobalSearchBar";

import { getUnreadProviderMessages } from "../../api/messageRequests";
import clinicList from "../../api/clinicList.json";


const ProviderSidePanel = ({ user, language, selectedLocation, onLocationChange }) => {
  const { t } = useTranslation(); // `language` prop no longer needed
  const clinicLocations = (clinicList[user.clinic_name] || []).map((loc, i) => ({
    value: `loc-${i}`,
    label: loc,
  }));

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1.5rem",
        padding: "0.6rem 1rem",
        marginBottom: "1rem",
        background: "#f8f9fa",
        borderRadius: "8px",
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#3eb1a6",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#adb5bd",
            marginRight: "4px",
          }}
        >
          {t("dashboard.patientControl.currentLocation")}
        </span>
        <span style={{ fontSize: "13px", fontWeight: 500, color: "#212529" }}>
          {selectedLocation ? selectedLocation.label : ""}
        </span>
      </div>

      <div style={{ position: "relative", flexShrink: 0 }}>
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#adb5bd",
            marginBottom: "0",
            display: "inline",
            marginRight: "8px",
          }}
        >
          {t("dashboard.patientControl.switchLocation")}
        </div>
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          style={{
            background: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            padding: "4px 10px",
            fontSize: "13px",
            color: "#495057",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "inherit",
          }}
        >
          {selectedLocation ? selectedLocation.label : ""}
          <span
            style={{
              fontSize: "9px",
              color: "#adb5bd",
              display: "inline-block",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </span>
        </button>

        {isOpen && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              zIndex: 100,
              overflow: "hidden",
              minWidth: "200px",
            }}
          >
            {clinicLocations.map((loc) => (
              <div
                key={loc.value}
                role="option"
                aria-selected={selectedLocation && loc.value === selectedLocation.value}
                onClick={() => { onLocationChange(loc); setIsOpen(false); }}
                onMouseEnter={(e) => {
                  if (!selectedLocation || loc.value !== selectedLocation.value)
                    e.currentTarget.style.background = "#f8f9fa";
                }}
                onMouseLeave={(e) => {
                  if (!selectedLocation || loc.value !== selectedLocation.value)
                    e.currentTarget.style.background = "transparent";
                }}
                style={{
                  padding: "9px 12px",
                  fontSize: "13px",
                  cursor: "pointer",
                  lineHeight: 1.4,
                  background: selectedLocation && loc.value === selectedLocation.value ? "#f0faf8" : "transparent",
                  color: selectedLocation && loc.value === selectedLocation.value ? "#3eb1a6" : "#495057",
                  fontWeight: selectedLocation && loc.value === selectedLocation.value ? 500 : 400,
                }}
              >
                {loc.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  const weekday = date.toLocaleString("en-US", { weekday: "short" });
  const datePart = date.toLocaleString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
  const timePart = date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${weekday} ${datePart} ${timePart}`;
};

const SELECTED_PATIENT = "patient";
const SELECTED_CAREGIVER_PATIENT = "caregiver_patient";

const PatientControl = ({
  user,
  patientList,
  caregiverList,
  setPatient,
  setUserIdForTrackedData,
}) => {
  const { t, language } = useTranslation();

  const [unreadPatientIds, setUnreadPatientIds] = useState([]);
  const [unreadCaregiverIds, setUnreadCaregiverIds] = useState([]);
  const [unreadSummary, setUnreadSummary] = useState([]);
  const [dismissedUnread, setDismissedUnread] = useState(false);
  useEffect(() => {
    if (user?.userType === "provider") {
      getUnreadProviderMessages()
        .then((res) => {
          const { patientIds = [], caregiverIds = [], unreadSummary = [] } = res.data;
          setUnreadPatientIds(patientIds);
          setUnreadCaregiverIds(caregiverIds);
          setUnreadSummary(unreadSummary);
        })
        .catch((err) => console.error("Failed to fetch unread messages:", err));
    }
  }, [user]);

  const [currentlySelectedType, setCurrentlySelectedType] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);
  const [selectedCaregiverPatientId, setSelectedCaregiverPatientId] =
    useState(null);

  const history = useHistory();

  const [submitting, setSubmitting] = useState(false);
  const [noPatientError, setNoPatientError] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");

  // Traveler location filtering
  const isTraveler = user.userType === "provider" && user.traveler === true;
  const clinicLocations = isTraveler
    ? (clinicList[user.clinic_name] || []).map((loc, i) => ({ value: `loc-${i}`, label: loc }))
    : [];
  const savedLocationLabel = localStorage.getItem(`selectedLocation_${user.user_id}`);
  const initialLocation = savedLocationLabel
    ? clinicLocations.find((loc) => loc.label === savedLocationLabel) || clinicLocations[0]
    : clinicLocations[0];
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);

  const handleLocationChange = (loc) => {
    localStorage.setItem(`selectedLocation_${user.user_id}`, loc.label);
    setSelectedLocation(loc);
  };

  useEffect(() => {
    if (!(user.userType == "caregiver" || user.userType == "provider"))
      history.push("/");
  }, []);

  const handleSubmit = (patient) => {
    setSubmitting(true);
    if (!patient) {
      setNoPatientError(true);
    } else {
      setPatient(patient);
      setUserIdForTrackedData(patient.caregiverId);
      history.push("/");
    }
    setSubmitting(false);
  };

  if (!user) {
    return (
      <Redirect
        to={{
          pathname: "/signIn",
        }}
      />
    );
  }

  const selectedPatientObj =
    selectedPatientId &&
    patientList.find((patient) => patient.patientId === selectedPatientId);

  const selectedCaregiverObj =
    selectedCaregiverId &&
    caregiverList.find(
      (caregiver) => caregiver.caregiverId === selectedCaregiverId
    );

  const selectedCaregiverPatientObj =
    selectedCaregiverPatientId &&
    caregiverList.find(
      (patient) => patient.patientId === selectedCaregiverPatientId
    );

  let currentlySelectedObj = null;
  if (currentlySelectedType === SELECTED_PATIENT) {
    currentlySelectedObj = selectedPatientObj;
  } else if (currentlySelectedType === SELECTED_CAREGIVER_PATIENT) {
    currentlySelectedObj = selectedCaregiverPatientObj;
  }

  // For traveler providers, filter both lists down to the selected clinic_location
  const filteredPatientList = isTraveler && selectedLocation
    ? (patientList || []).filter(p => p.clinic_location === selectedLocation.label)
    : patientList;

  const filteredCaregiverList = isTraveler && selectedLocation
    ? (caregiverList || []).filter(c => c.clinic_location === selectedLocation.label)
    : caregiverList;

  const patientListOptions = filteredPatientList
    ?.map((patient) => ({
      value: patient.patientId,
      label: `${patient.firstName} ${patient.lastName}${
        patient.externalId ? `, ID: ${patient.externalId}` : ""
      }${
        patient.incompletedRegister
          ? t("dashboard.patientControl.patientListOptions.incompleteRegistration")
          : ""
      } ${
        patient.patientStatus === "Released"
          ? t("dashboard.patientControl.patientListOptions.released")
          : ""
      }`,
      hasUnread: unreadPatientIds.includes(patient.patientId),
    }))
    .sort((a, b) => a?.label.localeCompare(b?.label));

  let uniqueCaregivers = [];
  filteredCaregiverList &&
    filteredCaregiverList.forEach((c) => {
      let isUnique = true;
      for (const unique of uniqueCaregivers) {
        if (unique.caregiverName == c.caregiverName) {
          isUnique = false;
          break;
        }
      }
      if (isUnique) {
        uniqueCaregivers.push(c);
      }
    });

  const caregiverOptions = uniqueCaregivers
    ?.map((caregiver) => ({
      value: caregiver.caregiverId,
      label: caregiver.caregiverName,
      hasUnread: unreadCaregiverIds.includes(caregiver.caregiverId),
    }))
    .sort((a, b) => a?.label.localeCompare(b?.label));

  const selectedCaregiverPatients =
    selectedCaregiverObj &&
    filteredCaregiverList.filter(
      (item) => item.caregiverName === selectedCaregiverObj.caregiverName
    );

  const selectedCaregiverPatientOptions =
    selectedCaregiverPatients &&
    selectedCaregiverPatients
      ?.map((patient) => ({
        value: patient.patientId,
        label: `${patient.firstName} ${patient.lastName}
        ${patient.externalId ? `, ID: ${patient.externalId}` : ""}
        ${
          patient.incompletedRegister
            ? t("dashboard.patientControl.patientListOptions.incompleteRegistration")
            : ""
        } 
        ${
          patient.patientStatus === "Released"
            ? t("dashboard.patientControl.patientListOptions.released")
            : ""
        }`,
      }))
      .sort((a, b) => a?.label.localeCompare(b?.label));

  const selectStyles = {
    option: (provided, { data, isFocused }) => {
      const baseBg = data.hasUnread ? "#3eb1a6" : provided.backgroundColor;
      const hoverBg = data.hasUnread ? "#379f95" : provided.backgroundColor;

      return {
        ...provided,
        backgroundColor: isFocused ? hoverBg : baseBg,
        color: data.hasUnread ? "white" : provided.color,
      };
    },
  };

  const hasUnreadMessages = unreadSummary.length > 0;

  return (
    <div>
      <Sallie
        text={
          noPatientError
            ? t("dashboard.patientControl.sallie.noPatientError")
            : user.userType === "provider"
            ? t("dashboard.patientControl.sallie.provider")
            : t("dashboard.patientControl.sallie.nonProvider")
        }
      />

      {user.userType === "provider" && hasUnreadMessages && !dismissedUnread && (
        <div
          style={{
            backgroundColor: "#3eb1a6",
            color: "white",
            borderRadius: "10px",
            padding: "1rem 1.5rem",
            margin: "0 1rem 1.25rem",
            position: "relative",
          }}
        >
          <button
            onClick={() => setDismissedUnread(true)}
            aria-label="Dismiss unread messages alert"
            style={{
              position: "absolute",
              top: "0.6rem",
              right: "0.75rem",
              background: "none",
              border: "none",
              color: "white",
              fontSize: "1rem",
              cursor: "pointer",
              lineHeight: 1,
              padding: "2px 4px",
              opacity: 0.8,
            }}
          >
            ✕
          </button>
          <p
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              marginBottom: "0.5rem",
              paddingRight: "1.5rem",
            }}
          >
            {t("dashboard.patientControl.unreadMessages.header")}
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: "1.25rem" }}>
            {unreadSummary.map((item) => (
              <li key={item.senderId}>
                <span style={{ fontWeight: 600 }}>{item.senderName}</span>
                {" "}
                <span
                  style={{
                    fontSize: "0.85rem",
                    backgroundColor: item.userType === "caregiver" ? "#e8f4f8" : "#e8f8f5",
                    color: item.userType === "caregiver" ? "#2980b9" : "#27ae60",
                    borderRadius: 4,
                    padding: "2px 6px",
                    fontWeight: 500,
                  }}
                >
                  {item.userType === "caregiver"
                    ? t("dashboard.patientControl.globalSearch.typeCaregiverPatient")
                    : t("dashboard.patientControl.globalSearch.typePatient")}
                </span>
                {item.clinicLocation && (
                  <span style={{
                    fontSize: "0.85rem",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 4,
                    padding: "2px 6px",
                    marginLeft: "4px",
                    fontWeight: 400,
                  }}>
                    {item.clinicLocation}
                  </span>
                )}
                {" at "}{formatTimestamp(item.timestamp)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="w-100 p-3 mx-auto">
        <ComponentWrapper>
          {user.userType === "provider" && user.traveler === true && (
            <h6 className="d-block mb-4">
              {t("dashboard.patientControl.selectLocation")}
            </h6>
          )}
          {user.userType === "provider" && user.traveler === true && <ProviderSidePanel user={user} language={language} selectedLocation={selectedLocation} onLocationChange={handleLocationChange} />}
          <h6 className="d-block mb-4">
            {filteredPatientList && filteredPatientList.length === 0
              ? t("dashboard.patientControl.patientSelect.noPatientError")
              : user.userType == "provider"
              ? t("dashboard.patientControl.patientSelect.provider")
              : t("dashboard.patientControl.patientSelect.nonProvider")}
          </h6>

          {user.userType === "provider" && (
            <>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "1rem 1.25rem",
                  marginBottom: "1.25rem",
                }}
              >
                <p
                  style={{
                    fontSize: "13.5px",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#6c757d",
                    margin: "0 0 0.6rem",
                  }}
                >
                  {t("dashboard.patientControl.quickSearch")}
                </p>
                <GlobalSearchBar
                  userType={user.userType}
                  patientList={filteredPatientList}
                  caregiverList={filteredCaregiverList}
                  onSelect={(patient, type) => {
                    if (type === "caregiver_patient") {
                      setUserIdForTrackedData(patient.caregiverId);
                    } else {
                      setUserIdForTrackedData(null);
                    }
                    handleSubmit(patient);
                  }}
                />
                <p
                  style={{
                    fontSize: "11px",
                    color: "#adb5bd",
                    margin: "-0.25rem 0 0",
                    textAlign: "center",
                  }}
                >
                  {t("dashboard.patientControl.searchesAcross")}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "1.25rem",
                }}
              >
                <div
                  style={{ flex: 1, height: "1px", backgroundColor: "#dee2e6" }}
                />
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#adb5bd",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("dashboard.patientControl.orBrowseByList")}
                </span>
                <div
                  style={{ flex: 1, height: "1px", backgroundColor: "#dee2e6" }}
                />
              </div>
            </>
          )}

          <Col>
            <Row className="d-flex justify-content-center">
              {filteredPatientList && (
                <Col md={4}>
                  <div className="mb-4">
                    <h5 className="mb-4">
                      {t("dashboard.patientControl.patientList.label")}
                      {unreadPatientIds.length > 0 && (
                        <span
                          style={{
                            color: "white",
                            backgroundColor: "#3eb1a6",
                            borderRadius: 4,
                            padding: "2px 6px",
                            marginLeft: 8,
                            fontSize: "0.85em",
                          }}
                        >
                          {t("dashboard.ui.newMessages")}
                        </span>
                      )}
                    </h5>
                    <Select
                      options={patientListOptions}
                      value={
                        selectedPatientId
                          ? patientListOptions.find(
                              (option) => option.value === selectedPatientId
                            )
                          : null
                      }
                      placeholder={
                        t("dashboard.patientControl.patientListOptions.select")
                      }
                      onChange={(selectedOption) => {
                        setSelectedPatientId(selectedOption.value);
                        setCurrentlySelectedType(SELECTED_PATIENT);
                        setSelectedCaregiverId(null);
                      }}
                      styles={selectStyles}
                    />
                  </div>
                </Col>
              )}

              {user.userType === "provider" && filteredCaregiverList && (
                <Col md={4}>
                  <div className="mb-4">
                    <h5 className="mb-4">
                      {t("dashboard.patientControl.caregiverList.label")}
                      {unreadCaregiverIds.length > 0 && (
                        <span
                          style={{
                            color: "white",
                            backgroundColor: "#3eb1a6",
                            borderRadius: 4,
                            padding: "2px 6px",
                            marginLeft: 8,
                            fontSize: "0.85em",
                          }}
                        >
                          {t("dashboard.ui.newMessages")}
                        </span>
                      )}
                    </h5>
                    <Select
                      options={caregiverOptions}
                      value={
                        selectedCaregiverId
                          ? caregiverOptions.find(
                              (caregiver) =>
                                caregiver.value === selectedCaregiverId
                            )
                          : null
                      }
                      placeholder={
                        t("dashboard.patientControl.caregiverList.select")
                      }
                      onChange={(selectedOption) => {
                        setSelectedCaregiverId(selectedOption.value);
                        setSelectedCaregiverPatientId(null);
                        setCurrentlySelectedType(null);
                        setSelectedPatientId(null);
                      }}
                      styles={selectStyles}
                    />
                  </div>
                  {selectedCaregiverId && (
                    <div className="mb-4">
                      <h5 className="mb-4">
                        {" "}
                        {t("dashboard.patientControl.caregiverList.patientList", { name: selectedCaregiverObj.caregiverName })}{" "}
                      </h5>
                      <Select
                        options={selectedCaregiverPatientOptions}
                        value={
                          selectedCaregiverPatientId
                            ? selectedCaregiverPatientOptions.find(
                                (option) =>
                                  option.value === selectedCaregiverPatientId
                              )
                            : null
                        }
                        placeholder={
                          t("dashboard.patientControl.patientListOptions.select")
                        }
                        onChange={(selectedOption) => {
                          setSelectedCaregiverPatientId(selectedOption.value);
                          setCurrentlySelectedType(SELECTED_CAREGIVER_PATIENT);
                        }}
                      />
                    </div>
                  )}
                </Col>
              )}
            </Row>

            <Row className="d-flex justify-content-center">
              {currentlySelectedObj && (
                <div className="mb-4">
                  <PillButton
                    variant={
                      currentlySelectedObj.patientStatus === "Released"
                        ? "gray"
                        : "secondary"
                    }
                    onClick={() => {
                      handleSubmit(currentlySelectedObj);
                    }}
                    disabled={
                      currentlySelectedObj.incompletedRegister &&
                      user.userType === "provider"
                    }
                  >
                    {t("dashboard.patientControl.submit", { name: currentlySelectedObj.firstName })}
                  </PillButton>
                </div>
              )}
            </Row>
          </Col>

          {user.userType === "provider" &&
            unreadCaregiverIds.length === 0 &&
            unreadPatientIds.length === 0 && (
              <div style={{ textAlign: "center", margin: "1.5rem 0" }}>
                <span
                  style={{
                    display: "inline-block",
                    backgroundColor: "#f0f0f0",
                    color: "#6c757d",
                    padding: "0.25rem 0.75rem",
                    borderRadius: "9999px",
                    fontSize: "0.875rem",
                  }}
                >
                  {t("dashboard.patientControl.noNewMessages")}
                </span>
              </div>
            )}
        </ComponentWrapper>

        {user.userType === "caregiver" ? (
          <div style={{ fontSize: "1.5rem" }}>
            {t("dashboard.patientControl.addPatient.0")}
            <SmLinkButton>
              <Link
                to={{
                  pathname: "/RegisterPatient",
                  state: { fromPatientControl: true },
                }}
              >
                {t("dashboard.patientControl.addPatient.1")}
              </Link>
            </SmLinkButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PatientControl;